import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import Request from '../../../../../../../helpers/request'
import * as url from '../../../../../../../helpers/url_helper'
import PaginationBar, { paginate, usePersistedPage } from '../../../../../../../components/Common/PaginationBar'

// Health-tab logs are denser than other tables; match Logs page size.
const PAGE_SIZE = 15

type EventLabel = 'periodic' | 'critical' | 'resolved' | 'node' | 'diag'
type HealthType = 'mem' | 'diag'

interface HealthRow {
    id: string
    masterMac: string
    uni: number
    type: HealthType
    event: number
    eventLabel: EventLabel | string
    data: any
    expiresAt: string | null
    createdAt: string
}

interface Watch {
    mac: string
    uni: number
    opType: number
    expiresAt: string
}

interface Props {
    placeId: string
    pcbs: any[]
    [key: string]: any
}

const EVENT_BADGE: Record<string, React.CSSProperties> = {
    periodic: { background: '#EFF8FF', color: '#175CD3', border: '1px solid #B2DDFF' },
    critical: { background: '#FEF3F2', color: '#B42318', border: '1px solid #FDA29B' },
    resolved: { background: '#ECFDF3', color: '#067647', border: '1px solid #ABEFC6' },
    node:     { background: '#FFF6ED', color: '#B54708', border: '1px solid #FECDAA' },
    diag:     { background: '#F4F3FF', color: '#5925DC', border: '1px solid #D9D6FE' },
}

const TH = ({ children }: { children: React.ReactNode }) => (
    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 13, color: '#858D9D', fontWeight: 500, background: '#F9FAFB' }}>
        {children}
    </th>
)

const TD = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
    <td style={{ padding: '12px 16px', fontSize: 14, color: '#344054', borderTop: '1px solid #E4E7EC', ...style }}>
        {children}
    </td>
)

// MAC normalized for matching: hex digits only, uppercase. Backend returns
// colon-less; the placePcbs have colon form. Compare on the normalized form.
const macHex = (s?: string | null): string => (s || '').replace(/[^0-9a-fA-F]/g, '').toUpperCase()

const OP_TYPE_LABEL: Record<number, string> = { 1: 'coil', 2: 'memory', 3: 'tasks' }

const formatTime = (iso: string): string => {
    try {
        return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'medium', timeZone: 'Asia/Kolkata' })
    } catch { return iso }
}

const formatIST = (iso: string | null): string => {
    if (!iso) return ''
    try {
        return new Date(iso).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Kolkata' }) + ' IST'
    } catch { return iso || '' }
}

const PlaceHealth = ({ placeId, pcbs }: Props) => {
    const [rows, setRows] = useState<HealthRow[]>([])
    const [watches, setWatches] = useState<Watch[]>([])
    const [loading, setLoading] = useState(false)
    const [filterEvent, setFilterEvent] = useState<'all' | EventLabel>('all')
    const [filterType, setFilterType] = useState<'all' | HealthType>('all')
    const [filterSince, setFilterSince] = useState<string>('1440')   // 24h default
    const [expanded, setExpanded] = useState<Set<string>>(new Set())
    const [busyUni, setBusyUni] = useState<string | null>(null)
    const [page, setPage] = usePersistedPage(`place-health-${placeId}`)

    // Master MAC for this place: WIFI_BLE_DUAL_PROXY → role 2 or 9 (see backend
    // cron.controller.js — 9 is the legacy form, both treated as Master).
    const masterPcb = (pcbs || []).find((p: any) =>
        p.placeId === placeId && (Number(p.role) === 2 || Number(p.role) === 9)
    )
    const placeMasterMacHex = macHex(masterPcb?.macAddress)

    const fetchHealth = () => {
        setLoading(true)
        const body: any = { placeId, limit: 500 }
        if (filterEvent !== 'all') body.eventLabel = filterEvent
        if (filterType !== 'all') body.type = filterType
        if (filterSince !== 'all') body.sinceMinutes = Number(filterSince)
        Request.post(url.HEALTH_LIST, body)
            .then((res: any) => setRows(Array.isArray(res?.data?.data) ? res.data.data : []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false))
    }

    const fetchWatches = () => {
        Request.post(url.HEALTH_WATCHES, {})
            .then((res: any) => setWatches(Array.isArray(res?.data?.data) ? res.data.data : []))
            .catch(() => setWatches([]))
    }

    useEffect(() => {
        fetchHealth()
        fetchWatches()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placeId, filterEvent, filterType, filterSince])

    const unwatch = (mac: string, uni: number) => {
        const key = `${mac}-${uni}`
        setBusyUni(key)
        Request.post(url.HEALTH_UNWATCH, { masterMac: mac, uni })
            .then((res: any) => {
                setWatches(Array.isArray(res?.data?.data?.watches) ? res.data.data.watches : [])
                toast.success('Watch stopped')
            })
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Failed to stop watch'))
            .finally(() => setBusyUni(null))
    }

    const toggleExpand = (id: string) => {
        setExpanded(prev => {
            const next = new Set(prev)
            next.has(id) ? next.delete(id) : next.add(id)
            return next
        })
    }

    // Only this place's watches (match by master MAC).
    const placeWatches = watches.filter(w => macHex(w.mac) === placeMasterMacHex)
    const { paged, totalPages, safePage } = paginate(rows, page, PAGE_SIZE)

    const summarize = (r: HealthRow): string => {
        const d = r.data || {}
        if (r.type === 'mem') {
            const parts = []
            if (d.int_free != null) parts.push(`free ${d.int_free}`)
            if (d.int_min != null) parts.push(`min ${d.int_min}`)
            if (d.int_largest != null) parts.push(`largest ${d.int_largest}`)
            if (d.episode_s) parts.push(`${d.episode_s}s`)
            if (d.stack_crit) parts.push('stack!')
            return parts.join(' · ')
        }
        if (r.type === 'diag') {
            const parts = []
            if (d.mqtt != null) parts.push(`mqtt ${d.mqtt}`)
            if (d.subs_acked != null && d.subs_exp != null) parts.push(`subs ${d.subs_acked}/${d.subs_exp}`)
            if (d.ble != null) parts.push(`ble ${d.ble}`)
            if (d.mqtt_disc != null) parts.push(`disc ${d.mqtt_disc}`)
            if (d.probe_rtt_ms != null && d.probe_rtt_ms !== -1) parts.push(`rtt ${d.probe_rtt_ms}ms`)
            if (d.uptime_s != null) parts.push(`up ${d.uptime_s}s`)
            return parts.join(' · ')
        }
        return ''
    }

    return (
        <div>
            {/* Active watches */}
            <div data-testid="watches-section" style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
                <h6 style={{ margin: '0 0 4px', fontWeight: 600 }}>Active health watches</h6>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#858D9D' }}>
                    Suspect nodes the backend is actively reading. Each watch is TTL-boxed and stops on its own. Replies appear in the table below as <code>node</code> events.
                </p>
                {placeWatches.length === 0 ? (
                    <span style={{ color: '#858D9D', fontSize: 13 }}>No active watches on this network</span>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <TH>Master MAC</TH>
                                <TH>Unicast</TH>
                                <TH>Read</TH>
                                <TH>Expires</TH>
                                <TH>Action</TH>
                            </tr>
                        </thead>
                        <tbody>
                            {placeWatches.map(w => (
                                <tr key={`${w.mac}-${w.uni}`}>
                                    <TD><code style={{ fontSize: 12 }}>{w.mac}</code></TD>
                                    <TD>{w.uni}</TD>
                                    <TD>{OP_TYPE_LABEL[w.opType] || w.opType}</TD>
                                    <TD>{formatIST(w.expiresAt)}</TD>
                                    <TD>
                                        <button
                                            data-testid={`unwatch-${w.uni}`}
                                            onClick={() => unwatch(w.mac, w.uni)}
                                            disabled={busyUni === `${w.mac}-${w.uni}`}
                                            style={{ padding: '4px 10px', fontSize: 12, border: '1px solid #475467', background: '#fff', color: '#475467', borderRadius: 4, cursor: busyUni ? 'not-allowed' : 'pointer' }}
                                        >
                                            Unwatch
                                        </button>
                                    </TD>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#858D9D', marginBottom: 4 }}>Event</label>
                    <select
                        data-testid="health-filter-event"
                        value={filterEvent}
                        onChange={e => { setFilterEvent(e.target.value as any); setPage(0) }}
                        style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #D0D5DD', borderRadius: 6 }}
                    >
                        <option value="all">All</option>
                        <option value="periodic">Periodic</option>
                        <option value="critical">Critical</option>
                        <option value="resolved">Resolved</option>
                        <option value="node">Node (watched)</option>
                        <option value="diag">Diagnostic</option>
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#858D9D', marginBottom: 4 }}>Type</label>
                    <select
                        data-testid="health-filter-type"
                        value={filterType}
                        onChange={e => { setFilterType(e.target.value as any); setPage(0) }}
                        style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #D0D5DD', borderRadius: 6 }}
                    >
                        <option value="all">All</option>
                        <option value="mem">Memory</option>
                        <option value="diag">Diagnostic</option>
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#858D9D', marginBottom: 4 }}>Since</label>
                    <select
                        data-testid="health-filter-since"
                        value={filterSince}
                        onChange={e => { setFilterSince(e.target.value); setPage(0) }}
                        style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #D0D5DD', borderRadius: 6 }}
                    >
                        <option value="60">Last 1 hour</option>
                        <option value="360">Last 6 hours</option>
                        <option value="1440">Last 24 hours</option>
                        <option value="10080">Last 7 days</option>
                        <option value="all">All available</option>
                    </select>
                </div>
            </div>

            {/* Health log table */}
            <div style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: 32, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>Loading health…</div>
                ) : (
                    <>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <TH>Time</TH>
                                <TH>Event</TH>
                                <TH>Type</TH>
                                <TH>Master MAC</TH>
                                <TH>Unicast</TH>
                                <TH>Summary</TH>
                                <TH>Details</TH>
                            </tr>
                        </thead>
                        <tbody>
                            {paged.length === 0 && (
                                <tr>
                                    <td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                        {rows.length === 0 ? 'No health logs in the selected range' : 'No rows match the current filters'}
                                    </td>
                                </tr>
                            )}
                            {paged.map(r => (
                                <React.Fragment key={r.id}>
                                    <tr>
                                        <TD><code style={{ fontSize: 12 }}>{formatTime(r.createdAt)}</code></TD>
                                        <TD>
                                            <span style={{ fontSize: 12, fontWeight: 500, padding: '2px 10px', borderRadius: 12, ...(EVENT_BADGE[String(r.eventLabel)] || EVENT_BADGE.periodic) }}>
                                                {r.eventLabel}
                                            </span>
                                        </TD>
                                        <TD>{r.type}</TD>
                                        <TD><code style={{ fontSize: 12 }}>{r.masterMac}</code></TD>
                                        <TD>{r.uni}</TD>
                                        <TD style={{ fontSize: 13, color: '#475467' }}>{summarize(r)}</TD>
                                        <TD>
                                            <button
                                                data-testid={`expand-${r.id}`}
                                                onClick={() => toggleExpand(r.id)}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#1a73e8', padding: 0 }}
                                            >
                                                {expanded.has(r.id) ? '▼ Hide' : '▶ View'}
                                            </button>
                                        </TD>
                                    </tr>
                                    {expanded.has(r.id) && (
                                        <tr>
                                            <td colSpan={7} style={{ padding: '0 16px 12px', background: '#F9FAFB' }}>
                                                <pre data-testid={`raw-${r.id}`} style={{ fontSize: 12, margin: 0, overflowX: 'auto', color: '#344054' }}>
                                                    {JSON.stringify(r.data, null, 2)}
                                                </pre>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                    <PaginationBar
                        page={safePage}
                        totalPages={totalPages}
                        total={rows.length}
                        pageSize={PAGE_SIZE}
                        onPage={setPage}
                    />
                    </>
                )}
            </div>
        </div>
    )
}

export default PlaceHealth
